// api_whip.ts — WHIP : probe codec, envoi offer, stats monitoring, démarrage/arrêt streaming

import { buildNegotiatedCodec } from '../../dataTypes/liveStreamManagment/utils_codec';
import { parseSdpCodec, extractCodecFromSdp, forceSdpCodecs, h264ProfilePriority, withStereoOpus } from '../../dataTypes/liveStreamManagment/utils_sdp';
import { createFillerAudioTrack } from '../../dataTypes/liveStreamManagment/utils_audio';
import { SignalingRefs, SignalingCallbacks } from '../../dataTypes/liveStreamManagment/types_signaling';
import { defaultRTCConfig } from '../../dataTypes/liveStreamManagment/utils_webrtc';
import { trackPeerConnection } from './api_webrtcBuffers';
import {
  WhipEncodingSettings, getActiveWhipEncoding, subscribeWhipEncoding, whipProfilePrefix,
} from '../../dataTypes/liveStreamManagment/whipEncodingSettings';
import { DIAGNOSTIC_PROBES_ENABLED } from '../debugManagment/remoteLogger';

/**
 * Pose sur les senders du programme les réglages d'encodage modifiables sans
 * renégociation. Appelée au démarrage puis à chaque « Valider » des réglages
 * pendant la diffusion. Le profil H264 et les options Opus ne passent que par
 * le SDP : ils attendent le prochain « On air ».
 */
const applyWhipEncodingToSenders = async (
  pc: RTCPeerConnection,
  enc: WhipEncodingSettings,
  callbacks: Pick<SignalingCallbacks, 'addSdpLog'>,
): Promise<void> => {
  const videoSender = pc.getSenders().find(s => s.track?.kind === 'video');
  if (videoSender?.track) {
    // Indice de contenu : sans lui, Chrome applique une heuristique sur une piste
    // issue de canvas.captureStream() qui penche vers "detail" (optimisée texte/
    // netteté) — sous charge, elle sacrifie la fluidité pour préserver le piqué.
    // 'motion' (défaut) est le réglage adapté à de la diffusion TV.
    try { videoSender.track.contentHint = enc.contentHint; } catch { /* non supporté */ }
    try {
      const params = videoSender.getParameters();
      if (!params.encodings || params.encodings.length === 0) params.encodings = [{}];
      params.encodings[0].maxBitrate = enc.maxBitrateKbps * 1000;
      // Cadence plafonnée (défaut : celle de la chaîne). Sans ce cap, une source qui
      // monterait au-dessus obligerait le ffmpeg aval à jeter des trames.
      params.encodings[0].maxFramerate = enc.maxFramerate;
      // Le programme est à l'antenne : il passe avant les angles d'un enregistrement
      // multi-angle, qui publient sur leurs propres PeerConnections. Chacune a son propre
      // contrôle de congestion et ignore les autres — cette priorité est le seul levier
      // qui hiérarchise ce que le navigateur sacrifie quand le lien montant sature.
      params.encodings[0].networkPriority = enc.networkPriority;
      // 1 (défaut) interdit tout sous-échantillonnage décidé par le navigateur.
      params.encodings[0].scaleResolutionDownBy = enc.scaleResolutionDownBy;
      // Sous contrainte de bande passante, "balanced" dégrade la résolution ET le
      // framerate. 'maintain-resolution' (défaut) garde la taille du canvas source —
      // nécessaire pour que la taille du fichier enregistré reste proportionnelle
      // au temps (estimation quota).
      params.degradationPreference = enc.degradationPreference;
      await videoSender.setParameters(params);
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: `📊 Vidéo: ${enc.maxBitrateKbps} kbps max, ${enc.maxFramerate} fps max, ÷${enc.scaleResolutionDownBy}, ${enc.degradationPreference}, contentHint ${enc.contentHint || 'aucun'}, priorité ${enc.networkPriority}` });
    } catch (e) {
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: `⚠ setParameters non supporté: ${(e as any).message}` });
    }
  }
  // Plafond d'envoi de la piste audio, au cas où Chrome garde un débit plus bas.
  const audioSender = pc.getSenders().find(s => s.track?.kind === 'audio');
  if (audioSender) {
    try {
      const ap = audioSender.getParameters();
      if (!ap.encodings || !ap.encodings.length) ap.encodings = [{}];
      ap.encodings[0].maxBitrate = enc.audioBitrateKbps * 1000;
      await audioSender.setParameters(ap);
    } catch (e) {
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: `⚠ setParameters audio non supporté: ${(e as any).message}` });
    }
  }
};

// ─── Probe codec ──────────────────────────────────────────────────────────────

export interface WhipProbeResult {
  codec: string;
  profile: string;
  answerSdp: string;
}

export const probeWhipCodec = async (
  whipUrl: string,
  forceSdp: (sdp: string) => string
): Promise<WhipProbeResult> => {
  const canvas = document.createElement('canvas');
  canvas.width = 320; canvas.height = 240;
  const ctx2d = canvas.getContext('2d');
  if (ctx2d) { ctx2d.fillStyle = 'black'; ctx2d.fillRect(0, 0, 320, 240); }
  const canvasStream = canvas.captureStream(1);

  const pc = new RTCPeerConnection(defaultRTCConfig());
  trackPeerConnection(pc, 'whip', 'sonde codec');
  const videoTrack = canvasStream.getVideoTracks()[0];
  pc.addTrack(videoTrack, canvasStream);

  let sessionCreated = false;

  try {
    const offer = await pc.createOffer();
    if (!offer.sdp) throw new Error('SDP vide');
    const filteredSdp = forceSdp(offer.sdp);
    await pc.setLocalDescription({ type: 'offer', sdp: filteredSdp });

    await new Promise<void>(resolve => {
      if (pc.iceGatheringState === 'complete') { resolve(); return; }
      const check = () => {
        if (pc.iceGatheringState === 'complete') {
          pc.removeEventListener('icegatheringstatechange', check); resolve();
        }
      };
      pc.addEventListener('icegatheringstatechange', check);
      setTimeout(resolve, 5000);
    });

    const res = await fetch(whipUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/sdp' },
      body: pc.localDescription!.sdp,
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`WHIP probe ${res.status}${body ? ': ' + body : ''}`);
    }
    sessionCreated = true;

    const answerSdp = await res.text();
    await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

    const codecs = extractCodecFromSdp(answerSdp);
    if (!codecs.videoCodec || !codecs.videoProfile) {
      throw new Error("Impossible d'extraire le codec depuis l'Answer WHIP");
    }
    return { codec: codecs.videoCodec, profile: codecs.videoProfile, answerSdp };
  } finally {
    canvasStream.getTracks().forEach(t => t.stop());
    if (sessionCreated) {
      try { await fetch(whipUrl, { method: 'DELETE' }); } catch { /* silencieux */ }
    }
    pc.close();
  }
};

// ─── Envoi offer / suppression session ───────────────────────────────────────

export const sendWhipOffer = async (
  whipUrl: string,
  sdp: string
): Promise<string> => {
  const res = await fetch(whipUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/sdp' },
    body: sdp,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`WHIP ${res.status}${body ? ': ' + body : ''}`);
  }
  return res.text();
};

export const deleteWhipSession = async (whipUrl: string): Promise<void> => {
  try { await fetch(whipUrl, { method: 'DELETE' }); } catch { /* silencieux */ }
};

// ─── Stats monitoring ─────────────────────────────────────────────────────────

export interface WhipStats {
  bytesSent:               number;
  packetsSent:             number;
  bitratKbps:              number;
  width:                   number;
  height:                  number;
  fps:                     number;
  packetsLost:             number;
  nackCount:               number;
  qualityLimitationReason: string;
  /** Bande passante montante estimée par le navigateur sur cette PeerConnection (kbps). */
  availableOutgoingKbps:   number;
  /** Aller-retour régie ↔ MediaMTX (ms). */
  rttMs:                   number;
}

export const collectWhipStats = async (
  pc: RTCPeerConnection,
  lastBytes: number,
  lastTimestamp: number
): Promise<WhipStats & { newLastBytes: number; newLastTimestamp: number }> => {
  const stats = await pc.getStats();
  let bytesSent = 0, packetsSent = 0, width = 0, height = 0, fps = 0;
  let packetsLost = 0, nackCount = 0, qualityLimitationReason = '';
  let availableOutgoingKbps = 0, rttMs = 0;

  stats.forEach(report => {
    if (report.type === 'outbound-rtp' && report.mediaType === 'video') {
      bytesSent                = report.bytesSent               || 0;
      packetsSent              = report.packetsSent             || 0;
      fps                      = report.framesPerSecond         || 0;
      nackCount                = report.nackCount               || 0;
      qualityLimitationReason  = report.qualityLimitationReason || '';
    }
    if (report.type === 'remote-inbound-rtp' && report.kind === 'video') {
      packetsLost = report.packetsLost || 0;
    }
    if (report.type === 'track' && report.kind === 'video') {
      width  = report.frameWidth  || 0;
      height = report.frameHeight || 0;
    }
    // La paire de candidats ICE retenue porte les deux seules mesures du LIEN lui-même,
    // par opposition à ce que l'encodeur a produit : la bande passante que le navigateur
    // estime disponible en montée, et l'aller-retour. Comparer bitratKbps à
    // availableOutgoingKbps donne la marge réelle — sans elle, on ne sait pas si le débit
    // observé est celui qu'on a demandé ou celui que le réseau a concédé.
    if (report.type === 'candidate-pair' && (report.nominated || report.selected)) {
      if (typeof report.availableOutgoingBitrate === 'number') {
        availableOutgoingKbps = report.availableOutgoingBitrate / 1000;
      }
      if (typeof report.currentRoundTripTime === 'number') {
        rttMs = report.currentRoundTripTime * 1000;
      }
    }
  });

  const now        = Date.now();
  const dt         = (now - lastTimestamp) / 1000;
  const bitratKbps = dt > 0 ? ((bytesSent - lastBytes) * 8) / dt / 1000 : 0;

  return {
    bytesSent, packetsSent, bitratKbps, width, height, fps,
    packetsLost, nackCount, qualityLimitationReason,
    availableOutgoingKbps, rttMs,
    newLastBytes: bytesSent, newLastTimestamp: now,
  };
};

export const startWhipStatsMonitoring = (
  pc: RTCPeerConnection,
  refs: Pick<SignalingRefs, 'whipStatsIntervalRef'>,
  callbacks: Pick<SignalingCallbacks, 'setWhipInfo' | 'addSdpLog'>
): void => {
  if (refs.whipStatsIntervalRef.current) clearInterval(refs.whipStatsIntervalRef.current);
  let lastBytes = 0, lastTimestamp = Date.now();
  let lastNackCount = 0, lastPacketsLost = 0;
  // 🔍 DIAGNOSTIC 2026-10-20 (cf. mémoire regie-desync-son-image-on-air) : le son
  // sort de la régie de plus en plus en retard. Combien de secondes de son et
  // d'image l'émetteur WebRTC a-t-il réellement reçues, rapportées au temps écoulé ?
  let avTick = 0, avBase: { wall: number; audio: number; frames: number } | null = null;
  // 🔍 DIAGNOSTIC 2026-10-20 : qu'est-ce qui borne la qualité de l'image envoyée ?
  // Encodeur (logiciel/matériel), profil réellement négocié, débit réel contre cible
  // et estimation du lien, QP moyen sur l'intervalle (plus bas = image plus fine).
  let encPrev: { t: number; bytes: number; qp: number; frames: number; lim: Record<string, number> } | null = null;

  refs.whipStatsIntervalRef.current = setInterval(async () => {
    if (!pc || pc.connectionState !== 'connected') return;
    if (avTick % 10 === 5) {
      try {
        const st = await pc.getStats();
        let out: any = null, codec: any = null, pair: any = null;
        st.forEach((r: any) => {
          if (r.type === 'outbound-rtp' && r.kind === 'video') out = r;
          if (r.type === 'candidate-pair' && (r.nominated || r.selected)) pair = r;
        });
        if (out) {
          codec = out.codecId ? st.get(out.codecId) : null;
          const cur = {
            t: out.timestamp, bytes: out.bytesSent || 0, qp: out.qpSum || 0,
            frames: out.framesEncoded || 0, lim: { ...(out.qualityLimitationDurations || {}) },
          };
          if (encPrev && cur.t > encPrev.t) {
            const dt = (cur.t - encPrev.t) / 1000, df = cur.frames - encPrev.frames;
            const lim = Object.keys(cur.lim)
              .map(k => [k, (cur.lim[k] - (encPrev!.lim[k] || 0)) / dt * 100] as const)
              .filter(([, pct]) => pct >= 1)
              .map(([k, pct]) => `${k} ${pct.toFixed(0)}%`).join(', ');
            console.warn(
              `[WHIP-ENC] ${out.encoderImplementation || '?'}${out.powerEfficientEncoder ? ' (matériel)' : ''}`
              + ` | ${codec?.mimeType || '?'} ${(codec?.sdpFmtpLine || '').match(/profile-level-id=\w+/i)?.[0] || ''}`
              + ` | ${out.frameWidth || 0}×${out.frameHeight || 0} ${(df / dt).toFixed(1)} i/s`
              + ` | ${((cur.bytes - encPrev.bytes) * 8 / dt / 1000).toFixed(0)} kb/s`
              + ` (cible ${((out.targetBitrate || 0) / 1000).toFixed(0)}, lien ${((pair?.availableOutgoingBitrate || 0) / 1000).toFixed(0)})`
              + ` | QP moyen ${df > 0 ? ((cur.qp - encPrev.qp) / df).toFixed(1) : '?'}`
              + ` | limitation ${out.qualityLimitationReason || 'none'}${lim ? ` [${lim}]` : ''}`,
            );
          }
          encPrev = cur;
        }
      } catch { /* silencieux */ }
    }
    if (DIAGNOSTIC_PROBES_ENABLED && ++avTick % 10 === 0) {
      try {
        const st = await pc.getStats();
        let audio = 0, frames = 0, fpsSrc = 0;
        st.forEach((r: any) => {
          if (r.type === 'media-source' && r.kind === 'audio') audio  = r.totalSamplesDuration || 0;
          if (r.type === 'media-source' && r.kind === 'video') { frames = r.frames || 0; fpsSrc = r.framesPerSecond || 0; }
        });
        const wall = performance.now() / 1000;
        if (!avBase) avBase = { wall, audio, frames };
        else {
          const dw = wall - avBase.wall, da = audio - avBase.audio, df = frames - avBase.frames;
          console.warn(
            `[AV-SENDER] depuis ${dw.toFixed(0)} s : son reçu ${da.toFixed(2)} s (${((da - dw) / dw * 60_000).toFixed(0)} ms/min)`
            + ` | images ${df} (${(df / dw).toFixed(2)} i/s, source ${fpsSrc} i/s)`,
          );
        }
      } catch { /* silencieux */ }
    }
    try {
      const result = await collectWhipStats(pc, lastBytes, lastTimestamp);
      lastBytes     = result.newLastBytes;
      lastTimestamp = result.newLastTimestamp;

      callbacks.setWhipInfo(prev => ({
        ...prev,
        bytesSent:               result.bytesSent,
        packetsSent:             result.packetsSent,
        bitratKbps:              result.bitratKbps,
        resolution:              result.width && result.height ? `${result.width}×${result.height}` : prev.resolution,
        fps:                     result.fps || prev.fps,
        state:                   pc.connectionState,
        iceState:                pc.iceConnectionState,
        packetsLost:             result.packetsLost,
        nackCount:               result.nackCount,
        qualityLimitationReason: result.qualityLimitationReason,
        // 0 = le navigateur n'a pas fourni la mesure sur ce tour : on garde la précédente
        // plutôt que d'afficher un trou dans un compteur qu'on lit pour juger d'une marge.
        availableOutgoingKbps:   result.availableOutgoingKbps || prev.availableOutgoingKbps,
        rttMs:                   result.rttMs                 || prev.rttMs,
      }));

      const deltaNack = result.nackCount   - lastNackCount;
      const deltaLost = result.packetsLost - lastPacketsLost;
      lastNackCount   = result.nackCount;
      lastPacketsLost = result.packetsLost;

      if (deltaNack > 0 || deltaLost > 0 || result.qualityLimitationReason === 'bandwidth') {
        callbacks.addSdpLog({
          direction: 'info',
          type: 'event',
          label: `⚠ Congestion → NACK+${deltaNack} | Lost+${deltaLost} | ${result.bitratKbps.toFixed(0)} kbps | limitation: ${result.qualityLimitationReason || 'none'}`,
        });
        console.warn(
          `[WHIP congestion] NACK+${deltaNack} Lost+${deltaLost} `
          + `${result.bitratKbps.toFixed(0)} kbps `
          + `fps=${result.fps} `
          + `limitation=${result.qualityLimitationReason || 'none'}`
        );
      }
    } catch { /* silencieux */ }
  }, 3000);
};

export const stopWhipStatsMonitoring = (
  refs: Pick<SignalingRefs, 'whipStatsIntervalRef'>
): void => {
  if (refs.whipStatsIntervalRef.current) {
    clearInterval(refs.whipStatsIntervalRef.current);
    refs.whipStatsIntervalRef.current = null;
  }
};

// ─── Démarrage / arrêt streaming ──────────────────────────────────────────────

export interface StartWhipStreamingParams {
  whipUrl:           string;
  currentStream:     MediaStream;
  canvasStreamRef:   React.MutableRefObject<MediaStream | null>;
  sourceVideoEl:     HTMLVideoElement | null;
  canvasEl:          HTMLCanvasElement | null;
  audioMixerDestRef: React.MutableRefObject<MediaStreamAudioDestinationNode | null>;
  refs:              SignalingRefs;
  callbacks:         SignalingCallbacks;
  negotiatedWhipCodec: any;
  initCanvasStream:  (videoEl: HTMLVideoElement, fps?: number) => MediaStream;
  canvasPreviewEl:   HTMLVideoElement | null;
}

export const startWhipStreaming = async ({
  whipUrl,
  currentStream,
  canvasStreamRef,
  sourceVideoEl,
  canvasEl,
  audioMixerDestRef,
  refs,
  callbacks,
  negotiatedWhipCodec,
  initCanvasStream,
  canvasPreviewEl,
}: StartWhipStreamingParams): Promise<void> => {
  callbacks.addSdpLog({ direction: 'info', type: 'event', label: `▶ Démarrage WHIP → ${whipUrl}` });

  const pc = new RTCPeerConnection(defaultRTCConfig());
  trackPeerConnection(pc, 'whip', 'programme');
  refs.peerConnectionRef.current = pc;

  // `disconnected` peut se résorber tout seul (perte réseau transitoire) mais
  // peut aussi y rester bloqué indéfiniment sans jamais atteindre `failed` —
  // contrairement à `failed`, rien alors ne remet `isStreaming` à false, et le
  // badge LIVE continue de clignoter sans connexion WHIP fonctionnelle. Un délai
  // de grâce laisse la résorption transitoire se faire avant de traiter le cas
  // comme un `failed`.
  let disconnectedTimer: ReturnType<typeof setTimeout> | null = null;
  const clearDisconnectedTimer = () => {
    if (disconnectedTimer) { clearTimeout(disconnectedTimer); disconnectedTimer = null; }
  };
  const treatAsFailed = () => {
    stopWhipStatsMonitoring(refs);
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: '🔄 Reconnexion automatique WHIP dans 3s...' });
    pc.close();
    refs.peerConnectionRef.current = null;
    callbacks.setIsStreaming(false);
    setTimeout(() => {
      if (refs.currentStreamRef.current) {
        callbacks.addSdpLog({ direction: 'info', type: 'event', label: '🔄 Tentative de reconnexion WHIP' });
        callbacks.startWebRTCStreaming();
      }
    }, 3000);
  };

  pc.onconnectionstatechange = () => {
    if (refs.peerConnectionRef.current !== pc) return;
    callbacks.setWhipInfo(prev => ({ ...prev, state: pc.connectionState, iceState: pc.iceConnectionState }));
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `WebRTC WHIP: ${pc.connectionState}` });

    if (pc.connectionState !== 'disconnected') clearDisconnectedTimer();

    if (pc.connectionState === 'connected') {
      startWhipStatsMonitoring(pc, refs, callbacks);
    }
    if (pc.connectionState === 'disconnected') {
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: '⚠ WHIP disconnected — 8s avant reconnexion si non résorbé' });
      disconnectedTimer = setTimeout(() => {
        disconnectedTimer = null;
        if (refs.peerConnectionRef.current === pc && pc.connectionState === 'disconnected') treatAsFailed();
      }, 8000);
    }
    if (pc.connectionState === 'failed') {
      treatAsFailed();
    }
    if (pc.connectionState === 'closed') {
      stopWhipStatsMonitoring(refs);
    }
  };

  pc.oniceconnectionstatechange = () => {
    if (refs.peerConnectionRef.current !== pc) return;
    callbacks.setWhipInfo(prev => ({ ...prev, iceState: pc.iceConnectionState }));
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `ICE WHIP: ${pc.iceConnectionState}` });
  };

  // ── Piste vidéo ─────────────────────────────────────────────────────────

  let whipVideoTrack: MediaStreamTrack;
  if (canvasStreamRef.current && canvasStreamRef.current.getVideoTracks().length > 0) {
    whipVideoTrack = canvasStreamRef.current.getVideoTracks()[0];
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🎨 Canvas track: ${whipVideoTrack.label || 'canvas'}` });
  } else if (sourceVideoEl && currentStream.getVideoTracks().length > 0) {
    if (sourceVideoEl.srcObject !== currentStream) {
      sourceVideoEl.srcObject = currentStream;
      await sourceVideoEl.play().catch(() => {});
      if (!sourceVideoEl.videoWidth) {
        await new Promise<void>(resolve => {
          sourceVideoEl.onloadedmetadata = () => resolve();
          setTimeout(resolve, 2000);
        });
      }
    }
    // 25 fps : aligné sur le -r 25 CFR de la chaîne ffmpeg aval (cf. useCanvasPipeline).
    const cs = initCanvasStream(sourceVideoEl, 25);
    if (canvasPreviewEl) { canvasPreviewEl.srcObject = cs; canvasPreviewEl.play().catch(() => {}); }
    whipVideoTrack = cs.getVideoTracks()[0];
  } else {
    whipVideoTrack = currentStream.getVideoTracks()[0];
  }

  if (canvasEl) {
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🖼 Canvas: ${canvasEl.width}×${canvasEl.height}` });
  }

  // ── Piste audio — mixer du provider ────────────────────────────────────
  //
  // audioMixerDestRef.current doit avoir été initialisé par le provider
  // (AudioMixerPipeline via initAudioMixer dans useCanvasPipeline) avant
  // d'appeler startWhipStreaming. C'est startWebRTCStreamingImpl dans
  // LiveStreamManager qui s'en assure.
  //
  // On ne crée plus de AudioContext standalone ici : ce fallback écrasait
  // audioMixerDestRef.current avec un contexte sans lien avec le provider,
  // rendant silencieux tous les sons/jingles connectés au pipeline.

  const mixerDest = audioMixerDestRef.current;

  if (!mixerDest) {
    // Ne devrait pas arriver : startWebRTCStreamingImpl appelle initAudioMixer
    // avant startWhipStreaming si audioMixerDestRef.current est null.
    callbacks.addSdpLog({ direction: 'received', type: 'error', label: '❌ audioMixerDestRef null au démarrage WHIP — streaming sans audio' });
    console.error('[startWhipStreaming] audioMixerDestRef.current est null — initAudioMixer n\'a pas été appelé');
  } else {
    const ctxState = (mixerDest.context as AudioContext).state;
    if (ctxState === 'suspended') {
      await (mixerDest.context as AudioContext).resume().catch(() => {});
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🎚 AudioContext relancé (était suspendu)` });
      // Un contexte qui ne repart PAS est un piège silencieux : sa destination
      // expose bien une piste 'live', donc le repli sur la piste de remplissage
      // ci-dessous ne se déclenche pas — mais aucun échantillon n'en sort, donc
      // aucun RTP, donc MediaMTX ne voit AUCUNE piste audio sur webrtc_UUID.
      // Vu de l'aval, c'est indiscernable d'une offre sans m-line audio.
      // console.warn pour que ce cas laisse enfin une trace côté serveur.
      const resumed = (mixerDest.context as AudioContext).state;
      if (resumed !== 'running') {
        console.warn(`[startWhipStreaming] AudioContext du mixer toujours '${resumed}' après resume() — la régie publiera probablement sans audio réel`);
      }
    } else if (ctxState === 'closed') {
      // Le contexte du provider a été fermé (ex : démontage/remontage du composant).
      // On logue l'erreur — la correction est de s'assurer que resetRegieContext
      // réinitialise bien le mixer avant de redémarrer le stream.
      callbacks.addSdpLog({ direction: 'received', type: 'error', label: '❌ AudioContext fermé au démarrage WHIP — streaming sans audio' });
      console.error('[startWhipStreaming] AudioContext du provider est fermé');
    } else {
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🎚 Mixer audio du provider (état: ${ctxState}) — sons/jingles inclus` });
    }
  }

  // ── La publication WHIP porte TOUJOURS une piste audio ──────────────────
  //
  // Sans piste audio, l'offre part sans m-line audio : `webrtc_UUID` n'expose
  // que de la vidéo et le microservice ffmpeg en aval en meurt (PMT du bus qui
  // change à la bascule suivante → mort de ffmpeg_final → coupure du path
  // public). Cf. createFillerAudioTrack() pour le détail et la mesure.
  //
  // `readyState` est vérifié, pas seulement la présence : une piste 'ended'
  // crée bien une m-line mais n'émet plus aucun RTP — donc, vu de MediaMTX,
  // c'est le même flux muet.
  const mixerAudioTrack = mixerDest?.stream.getAudioTracks()[0] ?? null;

  let whipAudioTrack: MediaStreamTrack | null = mixerAudioTrack;
  let fillerAudioDispose: (() => void) | null = null;

  if (mixerAudioTrack && mixerAudioTrack.readyState === 'live') {
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🔊 Track audio WHIP: ${mixerAudioTrack.label || mixerAudioTrack.id.slice(0, 8)} (état: ${mixerAudioTrack.readyState})` });
  } else {
    const reason = mixerAudioTrack ? `piste '${mixerAudioTrack.readyState}'` : 'aucune piste dans le mixer';
    // console.warn et pas seulement addSdpLog : le repli est anormal et doit
    // remonter dans les logs navigateur distants (files/private/logs/adminSide),
    // sans quoi la cause reste invisible côté serveur — c'est précisément ce qui
    // a manqué pour diagnostiquer la session du 2026-08-23.
    console.warn(`[startWhipStreaming] ${reason} — repli sur la piste audio de remplissage (bruit inaudible)`);
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `⚠ ${reason} — piste de remplissage inaudible publiée (m-line audio garantie)` });
    const filler       = createFillerAudioTrack();
    whipAudioTrack     = filler.track;
    fillerAudioDispose = filler.dispose;
  }

  // ── Ajout des tracks à la PeerConnection ────────────────────────────────

  const enc = getActiveWhipEncoding();
  const profilePrefix = whipProfilePrefix(enc.videoProfile);

  if (whipVideoTrack!) {
    const sender = pc.addTrack(whipVideoTrack, new MediaStream([whipVideoTrack]));

    try {
      const transceiver = pc.getTransceivers().find(t => t.sender === sender);
      if (transceiver && RTCRtpSender.getCapabilities) {
        const caps = RTCRtpSender.getCapabilities('video');
        if (caps) {
          // Même tri que forceSdpCodecs() — qui filtre l'Offer juste après — pour
          // que les deux mécanismes désignent le même profil : celui des réglages
          // d'encodage (High en automatique, cf. whipProfilePrefix), le barème de
          // h264ProfilePriority à défaut. Toujours le même d'une session à l'autre
          // sur un poste donné, donc la chaîne aval ne voit pas le profil changer.
          const profileOf = (c: RTCRtpCodecCapability) =>
            (c.sdpFmtpLine || '').match(/profile-level-id=([0-9a-fA-F]+)/i)?.[1]?.toLowerCase() || '';
          const h264Codecs = caps.codecs.filter(c => c.mimeType.toLowerCase() === 'video/h264');
          if (h264Codecs.length > 0) {
            const sorted = [...h264Codecs].sort((a, b) =>
              h264ProfilePriority(profileOf(a), profilePrefix) - h264ProfilePriority(profileOf(b), profilePrefix));
            transceiver.setCodecPreferences(sorted);
            callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🔒 setCodecPreferences: ${sorted[0]?.mimeType} ${sorted[0]?.sdpFmtpLine?.match(/profile-level-id=\w+/i)?.[0] || ''}` });
          }
        }
      }
    } catch (e) {
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: `⚠ setCodecPreferences non supporté: ${(e as any).message}` });
    }
  }

  if (whipAudioTrack) {
    // La piste de remplissage a son propre MediaStream : mixerDest peut être
    // null, c'est justement le cas qui déclenche le repli.
    pc.addTrack(whipAudioTrack, fillerAudioDispose ? new MediaStream([whipAudioTrack]) : mixerDest!.stream);
  }

  // Le contexte audio du remplissage doit être fermé avec la PeerConnection.
  // Porté par la pc elle-même plutôt que par un ref supplémentaire :
  // stopWhipStreaming() n'a que la pc sous la main, et `close()` ne déclenche
  // aucun évènement 'connectionstatechange' sur lequel se raccrocher.
  (pc as any).__fillerAudioDispose = fillerAudioDispose;

  // ── Négociation SDP ──────────────────────────────────────────────────────

  const offerRaw = await pc.createOffer();
  if (!offerRaw.sdp) throw new Error('SDP vide');
  callbacks.addSdpLog({ direction: 'sent', type: 'whip-offer-raw', label: 'Offer brute (avant filtre H264)', sdp: offerRaw.sdp, codec: parseSdpCodec(offerRaw.sdp) });

  const filteredSdp = forceSdpCodecs(offerRaw.sdp, profilePrefix);
  callbacks.addSdpLog({ direction: 'sent', type: 'whip-offer', label: 'Offer filtrée → MediaMTX', sdp: filteredSdp, codec: parseSdpCodec(filteredSdp) });

  await pc.setLocalDescription({ type: 'offer', sdp: filteredSdp });
  refs.whipOfferSdpRef.current = filteredSdp;

  await new Promise<void>(resolve => {
    if (pc.iceGatheringState === 'complete') { resolve(); return; }
    const check = () => { if (pc.iceGatheringState === 'complete') { pc.removeEventListener('icegatheringstatechange', check); resolve(); } };
    pc.addEventListener('icegatheringstatechange', check);
    setTimeout(resolve, 5000);
  });
  callbacks.addSdpLog({ direction: 'info', type: 'event', label: `ICE gathering: ${pc.iceGatheringState}` });

  const sdpToSend = pc.localDescription!.sdp;
  callbacks.addSdpLog({ direction: 'sent', type: 'whip-offer', label: 'SDP final envoyé à MediaMTX (avec ICE)', sdp: sdpToSend, codec: parseSdpCodec(sdpToSend) });

  const res = await fetch(whipUrl, { method: 'POST', headers: { 'Content-Type': 'application/sdp' }, body: sdpToSend });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    callbacks.addSdpLog({ direction: 'received', type: 'error', label: `❌ MediaMTX ${res.status}`, raw: { status: res.status, body } });
    throw new Error(`WHIP ${res.status}${body ? ': ' + body : ''}`);
  }

  const answerSdp = await res.text();
  callbacks.addSdpLog({ direction: 'received', type: 'whip-answer', label: 'Answer ← MediaMTX', sdp: answerSdp, codec: parseSdpCodec(answerSdp) });

  // Opus stéréo à haut débit (cf. withStereoOpus) : posé sur la description distante, car
  // c'est elle qui règle l'encodeur de Chrome. `answerSdp` reste l'original pour les journaux.
  const stereoAnswerSdp = withStereoOpus(answerSdp, enc.audioBitrateKbps * 1000, { stereo: enc.audioStereo, fec: enc.audioFec, dtx: enc.audioDtx });
  callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🎚️ Opus ${enc.audioStereo ? 'stéréo' : 'mono'} ${enc.audioBitrateKbps} kb/s, FEC ${enc.audioFec ? 'oui' : 'non'}, DTX ${enc.audioDtx ? 'oui' : 'non'} imposé dans la réponse` });
  await pc.setRemoteDescription({ type: 'answer', sdp: stereoAnswerSdp });
  refs.whipAnswerSdpRef.current = answerSdp;

  await applyWhipEncodingToSenders(pc, enc, callbacks);

  // Réglages modifiés pendant la diffusion : appliqués à chaud sur cette PeerConnection.
  const unsubscribeEncoding = subscribeWhipEncoding(next => {
    if (pc.connectionState === 'closed') { unsubscribeEncoding(); return; }
    applyWhipEncodingToSenders(pc, next, callbacks);
  });
  (pc as any).__unsubscribeEncoding = unsubscribeEncoding;

  const codecs = extractCodecFromSdp(answerSdp);
  callbacks.setWhipInfo(prev => ({
    ...prev,
    videoCodec:   codecs.videoCodec  || undefined,
    videoProfile: codecs.videoProfile || undefined,
    audioCodec:   codecs.audioCodec  || undefined,
    payloadType:  codecs.payloadType || undefined,
    offerSdp:     filteredSdp,
    answerSdp,
    state:        pc.connectionState,
    iceState:     pc.iceConnectionState,
  }));

  if (codecs.videoCodec && codecs.videoProfile) {
    refs.requiredCodecRef.current = { codec: codecs.videoCodec, profile: codecs.videoProfile };
    if (!negotiatedWhipCodec) {
      callbacks.setNegotiatedWhipCodec(buildNegotiatedCodec(codecs.videoCodec, codecs.videoProfile));
    }
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🔒 Codec confirmé: ${codecs.videoCodec} ${codecs.videoProfile}` });
    callbacks.setCodecProbeState('done');
  }

  callbacks.addSdpLog({ direction: 'info', type: 'event', label: '✅ WHIP établi — canvas compositing actif' });
  callbacks.setIsStreaming(true);
};

export const stopWhipStreaming = (
  refs: Pick<SignalingRefs, 'peerConnectionRef' | 'whipStatsIntervalRef'>,
  callbacks: Pick<SignalingCallbacks, 'setIsStreaming' | 'setWhipInfo'>
): void => {
  if (refs.peerConnectionRef.current) {
    // Cf. startWhipStreaming : libère l'AudioContext de la piste de remplissage
    // s'il y en a eu une. Sans effet dans le cas nominal.
    try { (refs.peerConnectionRef.current as any).__fillerAudioDispose?.(); } catch { /* déjà libéré */ }
    try { (refs.peerConnectionRef.current as any).__unsubscribeEncoding?.(); } catch { /* déjà désabonné */ }
    refs.peerConnectionRef.current.close();
    refs.peerConnectionRef.current = null;
  }
  stopWhipStatsMonitoring(refs);
  callbacks.setIsStreaming(false);
  callbacks.setWhipInfo(prev => ({ ...prev, state: 'stopped' }));
};